import React, { useEffect, useMemo, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';
import StatusBadge from '../components/StatusBadge';

interface CompanySuggestion {
  id: string;
  name: string;
  domain?: string;
  status: string;
  autoRejectNewLeads?: boolean;
}

const SubmitLead: React.FC = () => {
  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState('');
  const [domain, setDomain] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactSource, setContactSource] = useState<'PERSONAL' | 'BUSINESS'>('BUSINESS');
  const [salesTip, setSalesTip] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<CompanySuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const toast = useToast();

  const debouncedName = useMemo(() => companyName.trim(), [companyName]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (debouncedName.length < 2) {
        setSuggestions([]);
        return;
      }
      try {
        const res = await axios.get('/api/partner/companies/search', { params: { query: debouncedName } });
        if (!cancelled) setSuggestions(res.data || []);
      } catch {
        // ignore
      }
    };
    const timer = setTimeout(run, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [debouncedName]);

  const handleSelectCompany = async (c: CompanySuggestion) => {
    setCompanyName(c.name);
    if (c.domain) {
      setDomain(c.domain);
    }
    setShowSuggestions(false);
    try {
      const r = await axios.get('/api/partner/companies/lookup', { params: { name: c.name } });
      const data = r.data || {};
      if (data.domain) setDomain(data.domain);
      if (!contactName && data.lastContactName) setContactName(data.lastContactName);
      if (!contactEmail && data.lastContactEmail) setContactEmail(data.lastContactEmail);
      if (!contactPhone && data.lastContactPhone) setContactPhone(data.lastContactPhone);
    } catch {
      // ignore
    }
    if (c.autoRejectNewLeads) {
      toast.show(t('submitLead.partnerAutoRejectHint', t('submitLead.autoRejectHint')) as string, 'info');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post('/api/partner/leads', {
        companyName,
        domain,
        contactName,
        contactEmail,
        contactPhone,
        contactSource,
        salesTip,
      });
      toast.show(
        response.data?.status === 'REJECTED'
          ? (t('submitLead.partnerAutoRejectedSuccess', t('submitLead.autoRejectedSuccess')) as string)
          : (t('submitLead.partnerSuccess', t('submitLead.success')) as string),
        response.data?.status === 'REJECTED' ? 'info' : 'success'
      );
      setCompanyName('');
      setDomain('');
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setContactSource('BUSINESS');
      setSalesTip('');
    } catch (error: any) {
      const m = error.response?.data || t('submitLead.failure');
      toast.show(String(m), 'error');
    }
    finally { setLoading(false); }
  };

  return (
    <section className="max-w-2xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('submitLead.title')}</h1>
      <form className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden" onSubmit={handleSubmit}>
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('submitLead.partnerDetailsTitle', t('submitLead.detailsTitle'))}</div>
          <div className="text-xs text-gray-600">{t('submitLead.partnerDetailsSubtitle', t('submitLead.detailsSubtitle'))}</div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.company')}</label>
            <div className="relative">
              <input
                value={companyName}
                onChange={(e) => { setCompanyName(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="w-full px-3 py-2 border border-slate-200 rounded-md"
                placeholder={t('submitLead.companyPlaceholder') as string}
                required
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white/95 border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                  {suggestions.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => handleSelectCompany(s)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-100"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span className="break-words pr-2">{s.name}</span>
                        <StatusBadge kind="company" status={s.status} labelKeyPrefix="partnerStatus.company" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.domain')}</label>
            <input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md"
              placeholder={t('submitLead.domainPlaceholder') as string}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.contactName')}</label>
            <input
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.contactEmail')}</label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.contactPhone')}</label>
            <input
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.contactSource')}</label>
            <select
              value={contactSource}
              onChange={(e) => setContactSource(e.target.value as 'PERSONAL' | 'BUSINESS')}
              className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white"
            >
              <option value="BUSINESS">{t('submitLead.contactSourceBusiness')}</option>
              <option value="PERSONAL">{t('submitLead.contactSourcePersonal')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('submitLead.partnerNoteLabel', t('submitLead.salesTip'))}</label>
            <textarea
              value={salesTip}
              onChange={(e) => setSalesTip(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md"
              rows={4}
              placeholder={t('submitLead.partnerNotePlaceholder', t('submitLead.salesTipPlaceholder')) as string}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#47ABDB] disabled:opacity-60 text-white py-2 rounded-md font-semibold hover:bg-[#3d8fb8]"
          >
            {loading ? t('submitLead.submitting') : t('submitLead.partnerSubmit', t('submitLead.submit'))}
          </button>
        </div>
      </form>
    </section>
  );
};

export default SubmitLead;
